import { useState, type ChangeEvent } from 'react';
import {
  UseMutationResult,
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { APIError, DefaultQueryParams, Language, UserTrialsQueryParams } from '@data';
import {
  chatItemsKeys,
  userTrialContactsKeys,
  userTrialLocationsKeys,
  userTrialsKeys,
  volunteerTrialOverviewKeys,
} from '@data/constants';
import {
  ListContactView,
  ListLocationView,
  GetUserTrialsOrderBy,
  ListUserTrialView,
  TrialsOverviewView,
  UpdateMyTrialBody,
  UserTrialDetailView,
  UserTrialFeedBackView,
  UserTrialFeedbackBody,
  UserTrialsApiGetMyTrialLocationsRequest,
  UserTrialStatus,
  HistoryType,
  CurrentFlowView,
  MilestoneProgramOverviewView,
} from '@data/sdk/match';
import { MatchingApi } from '@data/utils/axios-instance';
import i18n from '../../../app/i18n';
import { useDebounce } from '../../../app/utils/hooks/useDebounce.hook';

export const useUserTrials = (params: UserTrialsQueryParams) =>
  useQuery<ListUserTrialView, Error>({
    queryKey: userTrialsKeys.list(params),
    queryFn: async () => {
      const response = await MatchingApi.userTrials.getMyTrials(params);
      return response.data;
    },
  });

export const useUserTrial = (id: string) => {
  return useQuery<UserTrialDetailView, Error>({
    queryKey: userTrialsKeys.detail(id),
    queryFn: async () => {
      const response = await MatchingApi.userTrials.getMyTrialDetail({
        id,
        language: i18n.language as Language,
      });
      return response.data;
    },
    enabled: !!id,
  });
};

export const useCreateTrialFeedback = (): UseMutationResult<
  UserTrialFeedBackView,
  APIError<UserTrialFeedbackBody>,
  { id: string; values: UserTrialFeedbackBody },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, values }) => {
      const response = await MatchingApi.userTrials.respondWithFeedback({
        id,
        userTrialFeedbackBody: values,
      });
      return response.data;
    },
    onSuccess: (_, { id }) =>
      queryClient.invalidateQueries({ queryKey: userTrialsKeys.detail(id) }),
  });
};

export const useStartStopTrialFlow = (): UseMutationResult<
  unknown,
  APIError<UpdateMyTrialBody>,
  { trialId: string; values: UpdateMyTrialBody },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ trialId, values }) => {
      const response = await MatchingApi.userTrials.updateMyTrialFlow({
        id: trialId,
        updateMyTrialBody: values,
      });
      return response.data;
    },
    onSuccess: (_, { trialId }) => {
      queryClient.invalidateQueries({ queryKey: chatItemsKeys.allByHistoryType(HistoryType.All) });
      queryClient.invalidateQueries({ queryKey: userTrialsKeys.detail(trialId) });
    },
  });
};

export const useUserTrialContacts = (id: string, params: DefaultQueryParams, enabled: boolean) =>
  useQuery<ListContactView, Error>({
    queryKey: userTrialContactsKeys.list(id),
    queryFn: async () => {
      const response = await MatchingApi.userTrials.getTrialContacts({ id, ...params });
      return response.data;
    },
    enabled: !!id && !!enabled,
  });

export const useUserTrialsOverview = (enabled = true) => {
  const defaultParams = {
    limit: 15,
    offset: 0,
    language: i18n.language as Language,
    orderBy: GetUserTrialsOrderBy.RemainingQuestions,
  };
  const defaultFilters = { orderBy: defaultParams.orderBy };

  const queryClient = useQueryClient();
  const request = useQuery<TrialsOverviewView, Error>({
    queryKey: volunteerTrialOverviewKeys.lists(),
    queryFn: async () => {
      const response = await MatchingApi.userTrials.getTrialsOverview();
      return response.data;
    },
    enabled,
  });
  const eligibleTrials = queryClient.getQueryData(
    userTrialsKeys.listPerStatus(UserTrialStatus.Eligible, defaultFilters),
  );

  const activeTrials = queryClient.getQueryData(
    userTrialsKeys.listPerStatus(UserTrialStatus.Active, defaultFilters),
  );

  if (request.data?.eligible && !eligibleTrials) {
    queryClient.prefetchInfiniteQuery({
      queryKey: userTrialsKeys.listPerStatus(UserTrialStatus.Eligible, defaultFilters),
      initialPageParam: { ...defaultParams, status: UserTrialStatus.Eligible },
      queryFn: async ({ pageParam }) => {
        const response = await MatchingApi.userTrials.getMyTrials(pageParam);
        return response.data;
      },
    });
  }

  if (request.data?.active && !activeTrials) {
    queryClient.prefetchInfiniteQuery({
      queryKey: userTrialsKeys.listPerStatus(UserTrialStatus.Active, defaultFilters),
      initialPageParam: { ...defaultParams, status: UserTrialStatus.Active },
      queryFn: async ({ pageParam }) => {
        const response = await MatchingApi.userTrials.getMyTrials(pageParam);
        return response.data;
      },
    });
  }

  return request;
};

export const useUserTrialLocationsInfinite = (trialId: string) => {
  const [query, setQuery] = useState<UserTrialsApiGetMyTrialLocationsRequest>({
    limit: 20,
    offset: 0,
    searchString: undefined,
    id: trialId,
  });

  const debouncedSearch = useDebounce(query.searchString);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetching,
    isFetchingNextPage,
    isFetchedAfterMount,
  } = useInfiniteQuery<ListLocationView, APIError>({
    queryKey: userTrialLocationsKeys.list({
      ...query,
      searchString: debouncedSearch,
    }),
    initialPageParam: {
      ...query,
      searchString: debouncedSearch,
    },
    queryFn: async ({ pageParam }) => {
      const response = await MatchingApi.userTrials.getMyTrialLocations(
        pageParam as UserTrialsApiGetMyTrialLocationsRequest,
      );
      return response.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      const nextOffset = allPages.reduce((prev, next) => prev + next.items.length, 0);

      return nextOffset < lastPage.count
        ? {
            limit: query.limit,
            offset: nextOffset,
            searchString: debouncedSearch || undefined,
            id: query.id,
          }
        : undefined;
    },
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    gcTime: 1000 * 60 * 60,
    staleTime: 1000 * 60 * 60,
  });

  const investigationSites = data?.pages.flatMap((page) => page.items) || [];

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setQuery({ ...query, searchString: e.target.value || undefined, offset: 0 });
  };

  const investigationSitesCount = data?.pages[0].count || 0;

  return {
    investigationSitesCount,
    investigationSites,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetching,
    handleSearchChange,
    searchString: query.searchString,
    isFetchingNextPage,
    isFetchedAfterMount,
  };
};

export const useCurrentFlow = (enabled = true) =>
  useQuery<CurrentFlowView, Error>({
    queryKey: userTrialContactsKeys.currentFlow(),
    queryFn: async () => {
      const response = await MatchingApi.userTrials.getCurrentFlow();
      return response.data;
    },
    enabled,
  });

export const useEnrolledTrialMilestoneProgram = (trialId: string) => {
  return useQuery<MilestoneProgramOverviewView, Error>({
    queryKey: userTrialsKeys.enrolledDetail(trialId),
    queryFn: async () => {
      const response = await MatchingApi.userTrials.getMilestoneProgram({ id: trialId });
      return response.data;
    },
    enabled: !!trialId,
  });
};
